import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, Button } from '@roger/ui-components';
import { AlertTriangle, Plus } from 'lucide-react';
import { FeatureFlag } from '../constants/featureFlags';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import CodingSearchForm, {
  CodingSearchParams,
  ClinicBranchFilter,
} from '../components/CodingSearchForm';
import { searchCodingVisits, CodingVisit } from '../services/rogerDocApi';
import NewVisitDialog from '../components/NewVisitDialog';
import { useCodingSearchParams } from '../hooks/useCodingSearchParams';
import { useSetSupportContext } from '../hooks/useSupportContext';

const CodingSearch: React.FC = () => {
  const { showError, hasPermission, hasFeatureFlag } = useStore(
    useShallow(state => ({
      showError: state.showError,
      hasPermission: state.hasPermission,
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const navigate = useNavigate();

  // URL-synced search params (nuqs is the single source of truth)
  const { searchParams, setSearchParams, clearSearchParams } =
    useCodingSearchParams();

  // Support context
  useSetSupportContext({
    label: 'Coding Search',
    subject: 'Coding Search - Support request',
    recipient: 'coding',
    hiddenContext: {
      searchParams: window.location.search,
    },
  });

  // Other state (not URL-synced)
  const [searchResults, setSearchResults] = useState<CodingVisit[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const initialSearchTriggered = useRef(false);

  // Check if user has permission to access Coding
  if (!hasPermission('coding:access')) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            You don't have permission to access Medical Coding features. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const [showNewVisitDialog, setShowNewVisitDialog] = useState(false);
  const enableNewVisitFeature = hasFeatureFlag(
    FeatureFlag.MEDICAL_CODING_ONLY_DOCUMENT_BASED
  );

  // Helper function to format search parameters for API
  const formatSearchParamsForAPI = (params: CodingSearchParams) => {
    const apiParams: Record<
      string,
      string | string[] | Record<string, boolean> | ClinicBranchFilter[]
    > = {};

    if (params.phrase.trim()) {
      apiParams.phrase = params.phrase.trim();
    }

    if (params.start_date) {
      apiParams.start_date = params.start_date.toISOString().split('T')[0];
    }

    if (params.end_date) {
      apiParams.end_date = params.end_date.toISOString().split('T')[0];
    }

    if (params.coding_status.length > 0) {
      apiParams.coding_status = params.coding_status;
    }

    if (params.visit_types.length > 0) {
      apiParams.visit_types = params.visit_types;
    }

    if (params.clinic_branches.length > 0) {
      apiParams.clinic_branches = params.clinic_branches;
    }

    if (params.tags && Object.keys(params.tags).length > 0) {
      const tags = { ...params.tags };
      // Composite "ready for coding" filter: when eval_scheduled=false AND
      // eval_completed=true, send ready_for_coding=true instead.
      // Roger-docs handles this as eval_scheduled:false OR eval_completed:true.
      if (tags.eval_scheduled === false && tags.eval_completed === true) {
        delete tags.eval_scheduled;
        delete tags.eval_completed;
        tags.ready_for_coding = true;
      }
      apiParams.tags = tags;
    }

    return apiParams;
  };

  const handleClearFilters = () => {
    clearSearchParams();
    setSearchResults([]);
    setHasSearched(false);
    setNextCursor(null);
    setTotalCount(null);
  };

  const handleSearch = async (params: CodingSearchParams) => {
    // Update URL params (single source of truth)
    setSearchParams(params);

    setSearching(true);
    setSearchResults([]);
    setNextCursor(null);
    setTotalCount(null);
    setHasSearched(false);

    try {
      const apiParams = formatSearchParamsForAPI(params);
      const response = await searchCodingVisits(apiParams);
      setSearchResults(response.items);
      setNextCursor(response.next_cursor);
      setTotalCount(response.total);
      setHasSearched(true);
    } catch (error: unknown) {
      console.error('Error searching for visits:', error);
      showError('Failed to search for visits. Please try again.');
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const apiParams = formatSearchParamsForAPI(searchParams);
      const response = await searchCodingVisits({
        ...apiParams,
        cursor: nextCursor,
      });
      setSearchResults(prev => [...prev, ...response.items]);
      setNextCursor(response.next_cursor);
      setTotalCount(response.total);
    } catch (error: unknown) {
      console.error('Error loading more visits:', error);
      showError('Failed to load more visits. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadAll = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const apiParams = formatSearchParamsForAPI(searchParams);
      let currentCursor: string | null = nextCursor;
      let accumulatedItems: CodingVisit[] = [];
      let finalTotal = totalCount;

      while (currentCursor) {
        const response = await searchCodingVisits({
          ...apiParams,
          cursor: currentCursor,
          page_size: 100,
        });
        accumulatedItems = [...accumulatedItems, ...response.items];
        currentCursor = response.next_cursor;
        finalTotal = response.total;
      }

      setSearchResults(prev => [...prev, ...accumulatedItems]);
      setNextCursor(null);
      setTotalCount(finalTotal);
    } catch (error: unknown) {
      console.error('Error loading all visits:', error);
      showError('Failed to load all visits. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleVisitSelected = (visit: CodingVisit) => {
    navigate(`/coding/${visit.diagnosis_id}`, {
      state: {
        search: window.location.search,
        visitStatus: visit.status,
      },
    });
  };

  const handleRerunSuccess = (diagnosisId: string) => {
    setSearchResults(prev =>
      prev.map(v =>
        v.diagnosis_id === diagnosisId ? { ...v, status: 'RUNNING' } : v
      )
    );
  };

  // Automatically search on component mount
  useEffect(() => {
    if (!initialSearchTriggered.current) {
      initialSearchTriggered.current = true;
      handleSearch(searchParams);
    }
  }, []);

  return (
    <div className='container mx-auto px-4 py-6'>
      <div className='flex flex-col gap-3 md:flex-row md:justify-between md:items-center mb-6'>
        <h1 className='text-2xl font-bold tracking-tight'>Medical Coding</h1>
        {enableNewVisitFeature && (
          <Button onClick={() => setShowNewVisitDialog(true)}>
            <Plus className='h-4 w-4 mr-2' />
            New Visit
          </Button>
        )}
      </div>

      {enableNewVisitFeature && (
        <NewVisitDialog
          open={showNewVisitDialog}
          onOpenChange={setShowNewVisitDialog}
          onVisitCreated={() => handleSearch(searchParams)}
        />
      )}

      <CodingSearchForm
        searchParams={searchParams}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        searching={searching}
        searchResults={searchResults}
        hasSearched={hasSearched}
        selectedVisit={null}
        loading={false}
        onVisitSelected={handleVisitSelected}
        onChangeSelection={() => {}}
        onRerunSuccess={handleRerunSuccess}
        totalCount={totalCount}
        hasMore={nextCursor !== null}
        onLoadMore={handleLoadMore}
        onLoadAll={handleLoadAll}
        loadingMore={loadingMore}
      />
    </div>
  );
};

export default CodingSearch;
